import { ChevronDown } from 'lucide-react';
import { createContext, type ReactNode, useContext, useId, useState } from 'react';
import { Button } from '../ui/Button';

const Scope = createContext<string | null>(null);

/** Only display preferences are stored, separately for each signed-in person and workspace. */
export function SettingsListPreferences({ personId, mspId, children }: { personId: string; mspId: string; children: ReactNode }) {
  return <Scope value={JSON.stringify([personId, mspId])}>{children}</Scope>;
}

interface Props<T> {
  id: string; title: string; items: readonly T[];
  newest?: (item: T) => string | null | undefined;
  children: (visible: readonly T[]) => ReactNode;
}

export function SettingsList<T>(props: Props<T>) {
  const scope = useContext(Scope);
  const storageKey = scope ? `rmm.settings-list:${scope}:${props.id}` : null;
  // Changing person/workspace must discard the previous person's in-memory state too.
  return <List key={storageKey ?? props.id} {...props} storageKey={storageKey} />;
}

function List<T>({ title, items, newest, children, storageKey }: Props<T> & { storageKey: string | null }) {
  const panel = useId();
  const [collapsed, setCollapsed] = useState(() => {
    try { return storageKey !== null && localStorage.getItem(storageKey) === 'collapsed'; } catch { return false; }
  });
  const [limit, setLimit] = useState(5);
  const ordered = newest ? [...items].sort((a, b) => {
    const time = (item: T) => { const value = newest(item); return value ? Date.parse(value) || 0 : 0; };
    return time(b) - time(a);
  }) : items;
  const more = limit < items.length;
  return <div className="min-w-0 space-y-2 [overflow-wrap:anywhere]">
    <h3>
      <button type="button" aria-expanded={!collapsed} aria-controls={panel}
        className="flex min-h-10 w-full items-center gap-2 rounded-md text-left text-[14px] font-medium focus-visible:outline-2 focus-visible:outline-brand"
        onClick={() => {
          const next = !collapsed;
          setCollapsed(next);
          try { if (storageKey) localStorage.setItem(storageKey, next ? 'collapsed' : 'expanded'); } catch { /* storage unavailable */ }
        }}>
        <ChevronDown aria-hidden="true" className={`size-4 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <span className="min-w-0">{title} <span className="text-ink-3">({items.length})</span></span>
      </button>
    </h3>
    <div id={panel} hidden={collapsed}>
      {children(ordered.slice(0, limit))}
      {items.length > 5 && <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button className="min-h-10" aria-controls={panel} aria-label={`${more ? 'Show more' : 'Show less'} ${title}`}
          onClick={() => setLimit(more ? limit + 5 : 5)}>{more ? 'Show more' : 'Show less'}</Button>
        <span className="text-[12.5px] text-ink-3" aria-live="polite">Showing {Math.min(limit, items.length)} of {items.length}</span>
      </div>}
    </div>
  </div>;
}
